import { makeValidate } from "mui-rff";
import * as Yup from 'yup';
import { USER_ROLE_VALUES } from './interfaces';
import { getMinEventDate } from './utils';

const dateOfBirthRegex = /^\d{4}-\d{2}-\d{2}$/;
const phoneNumberRegex = /^\+1-\d{3}-\d{3}-\d{4}$/;

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Enter a valid email").required("Email is a required field"),
  password: Yup.string().required("Password is a required field")
})

const registerSchema = Yup.object().shape({
  email: Yup.string().email("Enter a valid email").required("Email is a required field"),
  password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is a required field"),
  confirmPassword: Yup.string()
    .required("Confirm password is a required field")
    .oneOf([Yup.ref('password')], "Passwords must match"),
  firstName: Yup.string().required("First name is a required field"),
  lastName: Yup.string().required("Last name is a required field"),
  dateOfBirth: Yup.string()
    .required("Date of birth is a required field")
    .matches(dateOfBirthRegex, "Date of birth must be in yyyy-mm-dd format"),
  phoneNumber: Yup.string()
    .required("Phone number is a required field")
    .matches(phoneNumberRegex, "Phone number must match +1-XXX-XXX-XXXX"),
})

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is a required field"),
  confirmPassword: Yup.string()
    .required("Confirm password is a required field")
    .oneOf([Yup.ref('password')], "Passwords must match"),
})

const editUserSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is a required field"),
  lastName: Yup.string().required("Last name is a required field"),
  email: Yup.string().email('Enter a valid email').required('Email is a required field'),
  phoneNumber: Yup.string()
    .required("Phone number is a required field")
    .matches(phoneNumberRegex, "Phone number must match +1-XXX-XXX-XXXX"),
  dateOfBirth: Yup.string()
    .required("Date of birth is a required field")
    .matches(dateOfBirthRegex, "Date of birth must be in yyyy-mm-dd format"),
  startDate: Yup.string()
    .matches(dateOfBirthRegex, { message: "Start date must be in yyyy-mm-dd format", excludeEmptyString: true }),
  locationId: Yup.string()
    .required('Location is a required field')
    .uuid('Location ID must be a valid UUID'),
})

const editUserRoleSchema = Yup.object().shape({
  role: Yup.string()
    .required('Role is a required field')
    .oneOf([...USER_ROLE_VALUES], 'Role is invalid'),
})

const changePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is a required field'),
  newPassword: Yup.string().min(8, 'Password must be at least 8 characters').required('New password is a required field'),
  confirmNewPassword: Yup.string()
    .required('Confirm new password is a required field')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
})

const locationBaseSchema = {
  name: Yup.string().required('Location name is a required field'),
  addressLine1: Yup.string(),
  city: Yup.string(),
  province: Yup.string(),
  postalCode: Yup.string(),
  country: Yup.string(),
  phone: Yup.string(),
  email: Yup.string().email('Enter a valid email').optional(),
};

const createLocationSchema = Yup.object().shape(locationBaseSchema)

const editLocationSchema = Yup.object().shape({
  ...locationBaseSchema,
  isDisabled: Yup.string().oneOf(['true', 'false'], 'Status is invalid').required('Status is a required field'),
})

const quoteSchema = Yup.object().shape({
  customer_guid: Yup.string().required("Customer ID is a required field"),
  receive_amount: Yup.number().required("Receive Amount is a required field"),
  symbol: Yup.string().required("Asset Selection is a required field")
})

const cateringSchema = Yup.object().shape({
  groupSize: Yup.string()
    .oneOf(['small', 'large'], 'Group size is invalid')
    .required('Group size is a required field'),
  name: Yup.string(),
  email: Yup.string().email('Enter a valid email').required('Email is a required field'),
  phone: Yup.string()
    .required('Phone number is a required field')
    .matches(phoneNumberRegex, 'Phone number must match +1-XXX-XXX-XXXX'),
  numberOfPeople: Yup.string()
    .required('Number of people is a required field')
    .test('number-of-people-rules', function (value) {
      if (!value?.trim()) {
        return true;
      }

      const parsedNumberOfPeople = Number(value);
      if (!Number.isFinite(parsedNumberOfPeople) || parsedNumberOfPeople <= 0) {
        return this.createError({ message: 'Number of people must be greater than 0' });
      }

      const groupSize = this.parent.groupSize as 'small' | 'large' | undefined;
      if (groupSize === 'small' && parsedNumberOfPeople > 24) {
        return this.createError({ message: 'For less than 25 people, the maximum allowed is 24' });
      }

      if (groupSize === 'large' && parsedNumberOfPeople < 25) {
        return this.createError({ message: 'For more than 25 people, the minimum allowed is 25' });
      }

      return true;
    }),
  date: Yup.string().test(
    'min-event-date',
    'Date must be at least tomorrow',
    (value) => !value || value >= getMinEventDate(),
  ),
  address: Yup.string(),
  allergies: Yup.string(),
})


export const loginValidation = makeValidate(loginSchema)
export const registerValidation = makeValidate(registerSchema)
export const resetPasswordValidation = makeValidate(resetPasswordSchema)
export const quoteValidation = makeValidate(quoteSchema)
export const editUserValidation = makeValidate(editUserSchema)
export const editUserRoleValidation = makeValidate(editUserRoleSchema)
export const changePasswordValidation = makeValidate(changePasswordSchema)
export const createLocationValidation = makeValidate(createLocationSchema)
export const editLocationValidation = makeValidate(editLocationSchema)
export const cateringValidation = makeValidate(cateringSchema)